import { useQuery } from "@tanstack/react-query";
import { Loader2, StickyNote } from "lucide-react";
import { get } from "@/lib/api/_client";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./note-widget.i18n";

interface NoteData {
  id: string;
  clientId: string;
  title: string;
  content: string | null;
  summary: string | null;
}

interface NoteWidgetProps {
  note: {
    id: string;
    title: string;
    preview: string;
    updatedAt: string;
    authorName: string | null;
  };
  onActivityClick?: (url: string) => void;
}

export function NoteWidget({ note: initial, onActivityClick }: NoteWidgetProps) {
  const t = useT(T);
  const hasInitial = Boolean(initial.title && initial.preview);
  const { data, isLoading } = useQuery({
    queryKey: ["client-notes", "single", initial.id],
    queryFn: () => get<NoteData>(`/internal/client-notes/single/${initial.id}`),
    enabled: !hasInitial,
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-xs p-2 rounded bg-muted/50">
        <Loader2 className="h-3 w-3 animate-spin" />
        <span className="text-muted-foreground">{t.loading}</span>
      </div>
    );
  }

  const title = data?.title || initial.title || t.fallbackTitle;
  const preview = (data?.summary || data?.content || initial.preview || "").trim().slice(0, 100);
  const href = data?.clientId ? `/clients/${data.clientId}?tab=notes&id=${initial.id}` : null;

  const card = (
    <>
      <div className="h-6 w-6 rounded bg-primary/10 flex items-center justify-center flex-shrink-0">
        <StickyNote className="h-3 w-3 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{title}</div>
        {preview && <div className="text-muted-foreground truncate">{preview}</div>}
      </div>
    </>
  );

  return href ? (
    <button
      type="button"
      onClick={() => onActivityClick?.(href)}
      className="w-full text-left flex items-start gap-2 text-xs p-2 rounded bg-muted/50 hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer border border-transparent hover:border-primary/20"
    >
      {card}
    </button>
  ) : (
    <div className="w-full flex items-start gap-2 text-xs p-2 rounded bg-muted/50">{card}</div>
  );
}
